import React, { useCallback, useId, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

const SITE_KEY = (process.env.REACT_APP_RECAPTCHA_SITE_KEY || '').trim();

function createMathChallenge() {
  const a = Math.floor(Math.random() * 9) + 1;
  const b = Math.floor(Math.random() * 9) + 1;
  return { question: `${a} + ${b}`, answer: a + b };
}

export function useFormCaptcha() {
  const useRecaptcha = Boolean(SITE_KEY);
  const [mathChallenge, setMathChallenge] = useState(createMathChallenge);
  const [mathInput, setMathInput] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState('');

  const reset = useCallback(() => {
    if (useRecaptcha) {
      setRecaptchaToken('');
    } else {
      setMathChallenge(createMathChallenge());
      setMathInput('');
    }
  }, [useRecaptcha]);

  const validate = useCallback(() => {
    if (useRecaptcha) {
      return Boolean(recaptchaToken);
    }

    const trimmed = mathInput.trim();
    if (!trimmed) {
      return false;
    }

    const answer = Number(trimmed);
    return Number.isFinite(answer) && answer === mathChallenge.answer;
  }, [useRecaptcha, recaptchaToken, mathInput, mathChallenge.answer]);

  return {
    useRecaptcha,
    mathChallenge,
    mathInput,
    setMathInput,
    recaptchaToken,
    setRecaptchaToken,
    validate,
    reset,
  };
}

export function FormCaptchaField({
  className = '',
  useRecaptcha,
  mathChallenge,
  mathInput,
  onMathInputChange,
  onRecaptchaChange,
}) {
  const inputId = useId();

  return (
    <div className={`form-captcha ${className}`.trim()}>
      {useRecaptcha ? (
        <ReCAPTCHA
          sitekey={SITE_KEY}
          onChange={onRecaptchaChange}
          onExpired={() => onRecaptchaChange('')}
        />
      ) : (
        <>
          <label htmlFor={inputId} className="form-captcha__label">
            Security check: What is {mathChallenge.question}?
          </label>
          <input
            id={inputId}
            type="text"
            inputMode="numeric"
            name="captcha_answer"
            className="form-captcha__input"
            value={mathInput}
            onChange={(e) => onMathInputChange(e.target.value)}
            placeholder="Your answer"
            autoComplete="off"
          />
        </>
      )}
    </div>
  );
}

/** Read a form control by name — uses elements[] to avoid form.name conflicts. */
export function getFormField(form, fieldName) {
  const field = form?.elements?.[fieldName];
  if (!field) {
    return '';
  }
  return field.value ?? '';
}

function toUrlEncodedBody(fields) {
  if (typeof fields === 'string') {
    return fields;
  }

  const params = new URLSearchParams();
  Object.entries(fields).forEach(([key, value]) => {
    params.append(key, value ?? '');
  });
  return params.toString();
}

/** Fallback when fetch is blocked — POST via hidden form + iframe (load listener after submit). */
function submitViaHiddenForm(url, body) {
  const params = new URLSearchParams(body);

  return new Promise((resolve) => {
    const iframeName = `gas-target-${Date.now()}`;
    const iframe = document.createElement('iframe');
    iframe.name = iframeName;
    iframe.title = 'Form submission';
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText = 'position:absolute;width:0;height:0;border:0;visibility:hidden';

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = url;
    form.target = iframeName;
    form.acceptCharset = 'UTF-8';
    form.style.display = 'none';

    params.forEach((value, key) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = value;
      form.appendChild(input);
    });

    let settled = false;
    const finish = () => {
      if (settled) {
        return;
      }
      settled = true;
      window.setTimeout(() => {
        form.remove();
        iframe.remove();
      }, 1500);
      resolve(null);
    };

    document.body.appendChild(iframe);
    document.body.appendChild(form);
    form.submit();
    iframe.addEventListener('load', finish, { once: true });
    window.setTimeout(finish, 8000);
  });
}

/** Submit to Google Apps Script — fetch when possible, iframe POST as fallback. */
export async function submitToGoogleScript(url, fields) {
  const body = toUrlEncodedBody(fields);

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    });

    if (response.ok) {
      const text = (await response.text()).trim();
      if (text) {
        return text;
      }
    }
  } catch (_) {
    // CORS may block reading the response — fall through to form POST.
  }

  await submitViaHiddenForm(url, body);
  return null;
}

export default FormCaptchaField;
